import { Component } from '@theme/component';
import { ThemeEvents } from '@theme/events';

/**
 * @typedef {object} Refs
 * @property {import('./zoom-dialog').ZoomDialog} [zoomDialogComponent]
 * @property {import('./slideshow').Slideshow} [slideshow]
 * @property {HTMLElement[]} [media]
 *
 * @extends Component<Refs>
 */
export class MediaGallery extends Component {
  #controller = new AbortController();
  #hideClass = 'is-hidden-by-variant-filter';
  #styleId = 'variant-media-filter-style';

  connectedCallback() {
    super.connectedCallback();

    this.#ensureFilterStyles();

    const { signal } = this.#controller;
    const target = this.closest('.shopify-section, dialog');

    target?.addEventListener(ThemeEvents.variantUpdate, this.#handleVariantUpdate, { signal });
    this.refs.zoomDialogComponent?.addEventListener(
      ThemeEvents.zoomMediaSelected,
      this.#handleZoomMediaSelected,
      { signal }
    );

    requestAnimationFrame(() => {
      this.#filterMediaToSelectedVariant();
    });
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#controller.abort();
  }

  #handleVariantUpdate = (event) => {
    const source = event.detail?.data?.html;
    if (!source) return;

    const newMediaGallery = source.querySelector('media-gallery');
    if (!newMediaGallery) return;

    this.replaceWith(newMediaGallery);

    requestAnimationFrame(() => {
      newMediaGallery.filterMediaToSelectedVariant?.();
    });
  };

  #handleZoomMediaSelected = async (event) => {
    this.slideshow?.select(event.detail.index, undefined, { animate: false });
  };

  #ensureFilterStyles() {
    if (document.getElementById(this.#styleId)) return;

    const style = document.createElement('style');
    style.id = this.#styleId;
    style.textContent = `
      .${this.#hideClass} {
        display: none !important;
      }
    `;
    document.head.appendChild(style);
  }

  #norm(value) {
    return String(value || '')
      .toLowerCase()
      .trim()
      .replace(/^color[_-]/, '')
      .replace(/\s+/g, '_')
      .replace(/-+/g, '_');
  }

  #getSelectedColor() {
    const current =
      document.querySelector(
        'fieldset.variant-option--swatches[data-fieldset-index="0"] input[type="radio"][data-current-checked="true"]'
      ) ||
      document.querySelector('input[type="radio"][name^="Color-"][data-current-checked="true"]');

    if (current) {
      return current.value || current.getAttribute('aria-label') || '';
    }

    const checked =
      document.querySelector(
        'fieldset.variant-option--swatches[data-fieldset-index="0"] input[type="radio"]:checked'
      ) ||
      document.querySelector('input[type="radio"][name^="Color-"]:checked');

    return checked?.value || checked?.getAttribute('aria-label') || '';
  }

  #getMediaItems() {
    const items = this.querySelectorAll(
      'li[ref="media[]"].product-media-container, li[ref="media[]"], .product-media-container'
    );

    return [...items];
  }

  #getMediaToken(item) {
    const img = item.querySelector('img[alt]');
    const alt = img?.getAttribute('alt') || '';
    return alt ? this.#norm(alt) : '';
  }

  filterMediaToSelectedVariant() {
    this.#filterMediaToSelectedVariant();
  }

  #filterMediaToSelectedVariant() {
    const mediaItems = this.#getMediaItems();
    if (!mediaItems.length) return;

    const selectedToken = this.#norm(this.#getSelectedColor());

    mediaItems.forEach((item) => item.classList.remove(this.#hideClass));

    if (!selectedToken) return;

    const matchingItems = [];
    const nonMatchingItems = [];

    mediaItems.forEach((item) => {
      const token = this.#getMediaToken(item);

      if (!token || token === selectedToken) {
        matchingItems.push(item);
      } else {
        nonMatchingItems.push(item);
      }
    });

    if (!matchingItems.length) return;

    const firstMatch = matchingItems[0];
    const firstMatchIndex = mediaItems.indexOf(firstMatch);

    try {
      this.slideshow?.select?.(firstMatchIndex, undefined, { animate: false });
    } catch (e) {}

    requestAnimationFrame(() => {
      nonMatchingItems.forEach((item) => item.classList.add(this.#hideClass));
    });
  }

  zoom(index, event) {
    this.refs.zoomDialogComponent?.open(index, event);
  }

  preloadImage(index) {
    const zoomDialogMedia = this.refs.zoomDialogComponent?.refs.media[index];
    if (!zoomDialogMedia) return;

    this.refs.zoomDialogComponent?.loadHighResolutionImage(zoomDialogMedia);
  }

  get slideshow() {
    return this.refs.slideshow;
  }

  get media() {
    return this.refs.media;
  }

  get presentation() {
    return this.dataset.presentation;
  }
}

if (!customElements.get('media-gallery')) {
  customElements.define('media-gallery', MediaGallery);
}